import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, formatDateTime } from "@/lib/constants";
import { Shield, User } from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    if (user?.role === "admin") {
      api.get("/audit-log").then((r) => setAudit(r.data));
    }
  }, [user?.role]);

  return (
    <div className="p-3 sm:p-5 lg:p-6 max-w-[1100px]" data-testid="settings-page">
      <h1 className="font-editorial text-2xl sm:text-3xl text-pleiades-ink mb-1">Ayarlar</h1>
      <p className="text-[12px] sm:text-[13px] text-pleiades-slate mb-3 sm:mb-5">
        Ekip erişimi, roller ve sistem hareketleri
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Profile card */}
        <div className="border border-pleiades-border bg-white rounded-sm p-4">
          <div className="text-[12px] font-semibold text-pleiades-ink mb-3 flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" /> Hesabım
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-pleiades-muted">Ad Soyad</div>
              <div className="text-[13px]">{user?.name}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-pleiades-muted">E-posta</div>
              <div className="text-[13px]">{user?.email}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-pleiades-muted">Rol</div>
              <div className="text-[13px]">{ROLE_LABEL[user?.role]}</div>
            </div>
          </div>
        </div>

        {/* Users list */}
        <div className="lg:col-span-2 border border-pleiades-border bg-white rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-pleiades-border">
            <div className="text-[12px] font-semibold flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> Ekip ve Roller
            </div>
            <p className="text-[11px] text-pleiades-slate mt-0.5">
              Admin, Danışman ve Operasyon rolleri farklı erişim seviyelerine sahiptir.
            </p>
          </div>
          <table className="w-full text-[13px]">
            <thead className="bg-pleiades-sidebar/60 border-b border-pleiades-border text-[11px] uppercase tracking-wider text-pleiades-slate">
              <tr>
                <th className="text-left font-medium px-3 py-2">Ad</th>
                <th className="text-left font-medium px-3 py-2">E-posta</th>
                <th className="text-left font-medium px-3 py-2">Rol</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-pleiades-border/60" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2">{u.name}</td>
                  <td className="px-3 py-2 text-pleiades-slate">{u.email}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-pleiades-border bg-pleiades-sidebar">
                      {ROLE_LABEL[u.role]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit log */}
      {user?.role === "admin" && (
      <div className="mt-3 sm:mt-5 border border-pleiades-border bg-white rounded-sm overflow-hidden" data-testid="audit-log">
        <div className="px-4 py-3 border-b border-pleiades-border">
          <div className="text-[12px] font-semibold">Denetim Kayıtları</div>
          <p className="text-[11px] text-pleiades-slate mt-0.5">Sistem içi son hareketler</p>
        </div>
        <table className="w-full text-[13px]">
          <thead className="bg-pleiades-sidebar/60 border-b border-pleiades-border text-[11px] uppercase tracking-wider text-pleiades-slate">
            <tr>
              <th className="text-left font-medium px-3 py-2">Zaman</th>
              <th className="text-left font-medium px-3 py-2">Kullanıcı</th>
              <th className="text-left font-medium px-3 py-2">Eylem</th>
              <th className="text-left font-medium px-3 py-2">Detay</th>
            </tr>
          </thead>
          <tbody>
            {audit.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center px-3 py-8 text-[12px] text-pleiades-slate">
                  Denetim kaydı yok.
                </td>
              </tr>
            )}
            {audit.map((a) => (
              <tr key={a.id} className="border-b border-pleiades-border/60">
                <td className="px-3 py-2 text-pleiades-slate tabular-nums">{formatDateTime(a.at)}</td>
                <td className="px-3 py-2">{a.actor_name || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-pleiades-slate">{a.action}</td>
                <td className="px-3 py-2 text-[11px] text-pleiades-slate">
                  {a.detail ? JSON.stringify(a.detail) : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
